import React from "react";
import { variantURL, fallbackToOriginal } from "../../utils/imageVariant";

/**
 * UserCard component for displaying a user in the gallery grid
 *
 * @param {Object} props
 * @param {Object} props.user - User object with id, name, photoURL; optional
 *   subtitle (small line under the name) and tooltip (hover title) used by the
 *   recommended-guests surface
 * @param {boolean} props.isSelectionMode - Whether selection mode is active
 * @param {boolean} props.isSelected - Whether this user is selected
 * @param {Function} props.onClick - Click handler for the card
 * @param {Function} props.onExpand - Handler to expand photo (optional)
 * @param {boolean} props.compact - Whether to use compact (smaller) card size
 */
export function UserCard({
  user,
  isSelectionMode,
  isSelected,
  onClick,
  onExpand,
  compact = false,
}) {
  return (
    <div
      onClick={onClick}
      title={user.tooltip}
      className={`group relative rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-all duration-300 ${
        isSelectionMode && isSelected ? "ring-4 ring-primary" : ""
      } ${compact ? "gallery-card-compact" : "gallery-card"}`}
    >
      <div className="w-full h-full relative">
        {user.photoURL ? (
          <img
            src={variantURL(user.photoURL, 200)}
            onError={fallbackToOriginal(user.photoURL)}
            alt={user.name || "User"}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary to-primaryDark flex items-center justify-center">
            <span className="text-white text-2xl md:text-3xl lg:text-4xl font-bold">
              {(user.name || "?").charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        {/* Selection mode overlays */}
        {isSelectionMode && (
          <>
            {/* Expand button - always visible on mobile, hover on desktop */}
            {onExpand && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onExpand(user);
                }}
                className="absolute top-2 left-1.5 md:left-2 p-1 md:p-1.5 rounded-md bg-black/50 md:bg-black/40 backdrop-blur-sm opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200"
                title="Expand photo"
              >
                <svg
                  className="w-3.5 h-3.5 md:w-4 md:h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                  />
                </svg>
              </button>
            )}
            {/* Checkbox on the right */}
            <div className="absolute top-2 right-2">
              <div
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  isSelected
                    ? "bg-primary border-primary"
                    : "bg-white/80 dark:bg-gray-800/80 border-gray-300 dark:border-gray-600"
                }`}
              >
                {isSelected && (
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
            </div>
          </>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1.5 md:p-2 lg:p-3">
          <h3 className="text-white text-xs md:text-sm lg:text-base font-semibold drop-shadow-lg truncate">
            {user.name || "Unnamed User"}
          </h3>
          {user.subtitle && (
            <p className="text-white/80 text-[10px] md:text-xs drop-shadow truncate">
              {user.subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
